import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { OrderDirection } from "~/operations";

const getParamKey = (keys: string[]) => keys.flatMap((k) => k || []).join(".");

const getFieldKey = (prefix = "") => getParamKey([prefix, "field"]);

const getDirectionKey = (prefix = "") => getParamKey([prefix, "direction"]);

type Sort<Field = string> = {
  direction: OrderDirection;
  field: Field;
};

type UseSortParams = {
  validFields?: Array<string | null>;
  defaultSort: Sort;
  prefix?: string;
  baseUrl?: URL | string;
};

export interface UseSortReturn<T> {
  orderBy: T;
  handleSortClick: (id: string) => void;
  sortColumnUrl: (id: string) => URL;
  baseUrl: URL | string | undefined;
}

export function useSort<T extends Sort>({
  validFields = [],
  defaultSort,
  prefix,
  baseUrl,
}: UseSortParams) {
  const url = new URL(baseUrl || location.href);
  const [searchParams, setSearchParams] = useSearchParams();
  const fieldKey = getFieldKey(prefix);
  const directionKey = getDirectionKey(prefix);

  const paramField = searchParams.get(fieldKey) || defaultSort.field;
  const paramDirection =
    searchParams.get(directionKey) || defaultSort.direction;
  const field =
    validFields.length !== 0 && validFields.includes(paramField)
      ? paramField
      : defaultSort.field;
  const direction = (paramDirection || defaultSort.direction) as OrderDirection;

  const handleSortClick = (id: string) => {
    const url = sortColumnUrl(id);
    setSearchParams(url.searchParams);
  };

  const orderBy = {
    direction,
    field,
  } as T;

  const reverse = (direction: OrderDirection) => {
    return direction === OrderDirection.Asc
      ? OrderDirection.Desc
      : OrderDirection.Asc;
  };

  const sortColumnUrl = useCallback(
    (id: string) => {
      if (field === id) {
        url.searchParams.set(directionKey, reverse(direction));
      } else {
        url.searchParams.set(directionKey, direction);
      }
      url.searchParams.set(fieldKey, id);
      return url;
    },
    [url, prefix, field, direction],
  );

  const result: UseSortReturn<T> = {
    orderBy,
    handleSortClick,
    sortColumnUrl,
    baseUrl,
  };
  return result;
}
